import React, { useEffect, useState } from 'react';
import { useOidc } from '@axa-fr/react-oidc';
import { ThemeProvider } from 'mintel-react-ui';
import MintelNavigation from 'mintel-navigation/MintelNavigation';
import { Route, Router, Switch } from 'wouter';
import jwtDecode from 'jwt-decode';
import * as Sentry from '@sentry/react';
import Favicon from 'react-favicon';
import { User } from 'configcat-react';
import { withFaroProfiler } from '@grafana/faro-react';
import { useIntercom } from 'react-use-intercom';

import useOidcAccessToken from '../useOidc';
import FederationWrapper from '../FederationWrapper';
import pages from '../Pages';
import { AppShellErrorPage as ErrorPage } from '../ErrorPage';
import IntercomPage from '../IntercomRedirect';
import { getTestRoutes, TestPages } from '../TestRoutes';
import styles from './App.scss';
import AuthWrapper, { CustomAuth } from '../AuthWrapper';
import useMixpanel from '../useMixpanel/useMixpanel';
import Page from './Page';
import useAccountManager from '../../hooks/useAccountManager';
import usePortalJwt from '../../hooks/usePortalJwt';
import { portalUrl } from '../../configuration';
import usePortalAccessToken from '../../hooks/usePortalAccessToken';

MintelNavigation.displayName = 'MintelNavigation';
const WrappedMintelNavigation = withFaroProfiler(MintelNavigation);

// Translation map between values from the CWA codebase to the theme values used by mintel-navigation
const jwtThemeMap: { [key in JwtTheme]: Theme } = {
  'theme-clients': 'default',
  'theme-compere': 'compere',
};

/**
 * Component for logging out. Triggers the logout handler from react-oidc on render.
 */
function Logout() {
  const { logout } = useOidc();
  useEffect(() => {
    logout();
  }, [logout]);
  return <div />;
}

/**
 * Main component of the app shell.
 * @constructor
 */
const App = (): JSX.Element => {
  const { login, renewTokens } = useOidc();
  const { accessToken: oidcAccessToken } = useOidcAccessToken();
  const decodedOidcAccessToken: AuthPayLoad | undefined = oidcAccessToken
    ? jwtDecode(oidcAccessToken)
    : undefined;
  const masquerading = !!decodedOidcAccessToken?.act;
  // Full portal access token
  const { data: accessToken } = usePortalAccessToken();
  // Smaller portal auth token
  const { data: portalJwt } = usePortalJwt();
  const [theme, setTheme] = useState<Theme | ''>('');
  const [navProps, setNavProps] = useState<MintelNavOptions>({});
  const { data: accountManager } = useAccountManager();
  const decodedAccessToken: PortalAccessPayload | undefined = accessToken
    ? jwtDecode(accessToken)
    : undefined;
  const jwtTheme =
    jwtThemeMap[
      decodedAccessToken?.mntl?.access?.clients?.branding ?? 'theme-clients'
    ];

  const configureNavigation = (newNavProps: MintelNavOptions) => {
    setNavProps(newNavProps);
  };

  const startTracking = useMixpanel(
    accessToken,
    oidcAccessToken,
    accountManager,
  );

  useEffect(() => {
    document.documentElement.lang = decodedAccessToken?.mntl?.lang || 'en';
  }, [decodedAccessToken?.mntl?.lang]);

  const { update } = useIntercom();
  useEffect(() => {
    if (decodedAccessToken) {
      if (SENTRY_DSN !== '') {
        Sentry.setUser({ email: decodedAccessToken.mntl.mail });
      }

      if (!masquerading && update) {
        update({
          userHash: decodedAccessToken?.mntl?.intercom_hash,
          userId: `${decodedAccessToken.mntl.usr_id}`,
        });
      }
    }
  }, [decodedAccessToken, masquerading, update]);

  const userTheme: Theme = theme || jwtTheme;

  let userObject: User | undefined;
  const userId = decodedAccessToken?.mntl.usr_id;
  const userOrg = decodedAccessToken?.mntl.org;
  if (userId && userOrg) {
    userObject = {
      email: decodedAccessToken?.mntl?.mail,
      identifier: userId.toString(),
      custom: { organization: userOrg },
    };
  }

  return (
    <div className={styles.root}>
      <Favicon
        url={
          S3_FOLDER +
          (userTheme === 'compere' ? 'comperemedia.ico' : 'mintel.ico')
        }
      />
      <ThemeProvider theme={userTheme}>
        <WrappedMintelNavigation
          noSiteNav
          user={{
            accessJWT: accessToken,
            jwt: portalJwt,
            oauthAccessJWT: oidcAccessToken,
          }}
          theme={userTheme}
          loginLink={() => login()}
          logoutLink={() => {
            localStorage.clear();
            window.location.href = `${portalUrl}/portal/logout`;
          }}
          showLanguage={false}
          accountManagerEmail={accountManager}
          showNotifications={!!oidcAccessToken}
          notificationCenterUrl={NOTIFICATION_CENTER_URL}
          showLoading={oidcAccessToken && !accessToken}
          refreshSession={renewTokens}
          enableLeapLink
          contentHref="#main"
          {...navProps}
        >
          <div className={styles.appWrapper}>
            <Router
              key="router"
              matcher={(pattern, path) =>
                path.startsWith(pattern) ? [true, {}] : [false, null]
              }
            >
              <Switch>
                <>
                  {pages.map(
                    ({
                      Component,
                      path,
                      configcatProjectKey,
                      hideIntercom = false,
                      customAuth = false,
                      loadBeforePortal = false,
                    }) => (
                      <Route path={path} key={path}>
                        <CustomAuth
                          customAuth={customAuth}
                          accessLoaded={
                            loadBeforePortal
                              ? !!oidcAccessToken
                              : !!decodedAccessToken
                          }
                        >
                          <FederationWrapper
                            pageName={path}
                            userObject={userObject}
                            userName={decodedAccessToken?.mntl?.full_name}
                            setTheme={setTheme}
                            configureNavigation={configureNavigation}
                            portalJwt={portalJwt}
                            configcatProjectKey={configcatProjectKey}
                            hideIntercom={hideIntercom}
                          >
                            {(
                              setThemeFn,
                              configureNavigationFn,
                              configureSidebarFn,
                              featureFlags,
                            ) => (
                              <Page
                                Component={Component}
                                path={path}
                                configureSidebar={configureSidebarFn}
                                userDetails={
                                  decodedAccessToken?.mntl !== undefined
                                    ? {
                                        email: decodedAccessToken?.mntl?.mail,
                                        name: decodedAccessToken?.mntl
                                          ?.full_name,
                                        org: decodedAccessToken?.mntl?.org,
                                        locale: decodedAccessToken?.mntl?.lang,
                                      }
                                    : undefined
                                }
                                oauthAccessToken={oidcAccessToken}
                                accessDetails={decodedAccessToken}
                                portalJwt={portalJwt}
                                startTracking={startTracking}
                                setTheme={setThemeFn}
                                configureNavigation={configureNavigationFn}
                                featureFlags={featureFlags}
                              />
                            )}
                          </FederationWrapper>
                        </CustomAuth>
                      </Route>
                    ),
                  )}
                </>
                {/* Route for testing pages. */}
                {decodedAccessToken?.mntl?.mail?.endsWith('@mintel.com') ? (
                  <>
                    <Route path="/test-pages">
                      <TestPages />
                    </Route>
                    {getTestRoutes(configureNavigation)}
                  </>
                ) : (
                  <></>
                )}
                {/* Routes for error pages */}
                <Route path="/500-error" key="500error">
                  <ErrorPage errorCode={500} />
                </Route>
                <Route path="/503-error" key="503error">
                  <ErrorPage errorCode={503} />
                </Route>
                <Route path="/404-error" key="404error">
                  <ErrorPage errorCode={404} />
                </Route>
                <Route path="/403-error" key="403error">
                  <ErrorPage errorCode={403} />
                </Route>
                {/* Route for redirecting comperemedia user to Intercom help center */}
                <Route path="/intercom-redirect" key="intercomRedirect">
                  <AuthWrapper>
                    <IntercomPage />
                  </AuthWrapper>
                </Route>
                {/* Default Route for pages that can't be found, 404 Error. */}
                <Route>
                  <AuthWrapper>
                    <ErrorPage errorCode={404} />
                  </AuthWrapper>
                </Route>
              </Switch>
            </Router>
          </div>
        </WrappedMintelNavigation>
      </ThemeProvider>
      <Route path="/logout">
        <Logout />
      </Route>
    </div>
  );
};

export default withFaroProfiler(App);
